import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"

/**
 * Button Component - Centralized button styling system
 * 
 * Variants:
 * - default/primary: Main CTA actions (Save, Create, Apply)
 * - secondary: Secondary actions (Cancel, Close)
 * - outline: Edit actions, less prominent buttons
 * - ghost: Subtle actions (Refresh, View, row actions)
 * - destructive: Dangerous actions (Delete, Disable)
 * - link: Text-only link-style buttons
 * - success: Positive confirmations (optional)
 * 
 * Sizes:
 * - sm: Small buttons, compact areas
 * - default: Standard button size
 * - lg: Large/prominent CTAs
 * - icon: Icon-only buttons (square)
 * 
 * Admin Action Mapping:
 * - Save/Create/Apply → default (primary)
 * - Cancel/Close → secondary or ghost
 * - Edit → outline
 * - Delete/Disable → destructive
 * - Refresh/View/Open → ghost or outline
 * - Row action menu → ghost + icon size
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // Primary - Main CTA (Save, Create, Apply)
        default:
          "bg-indigo-500/90 text-white shadow-sm hover:bg-indigo-500 focus-visible:ring-indigo-500",
        // Secondary - Secondary actions (Cancel, Close)
        secondary:
          "bg-white/10 text-slate-100 shadow-sm hover:bg-white/20 focus-visible:ring-slate-400",
        // Outline - Edit actions, less prominent
        outline:
          "border border-white/20 bg-transparent text-slate-200 shadow-sm hover:bg-white/10 hover:text-white focus-visible:ring-slate-400",
        // Ghost - Subtle actions (Refresh, View, row actions)
        ghost: 
          "text-slate-400 hover:bg-white/10 hover:text-slate-200 focus-visible:ring-slate-400",
        // Destructive - Dangerous actions (Delete, Disable) - Muted red for modern look
        destructive:
          "bg-red-500/80 text-white shadow-sm hover:bg-red-500/90 focus-visible:ring-red-500",
        // Destructive Outline - Less prominent destructive
        "destructive-outline":
          "border border-red-500/30 bg-transparent text-red-400 hover:bg-red-500/10 hover:text-red-300 focus-visible:ring-red-500",
        // Success - Positive confirmations
        success:
          "bg-emerald-600 text-white shadow-sm hover:bg-emerald-500 focus-visible:ring-emerald-500",
        // Link - Text-only link-style
        link: 
          "text-indigo-400 underline-offset-4 hover:text-indigo-300 hover:underline focus-visible:ring-indigo-500",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-6",
        icon: "h-9 w-9",
        "icon-sm": "h-7 w-7",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, isLoading = false, children, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      disabled={isLoading || props.disabled}
      {...props}
    >
      {isLoading ? (
        <>
          <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          {children}
        </>
      ) : (
        children
      )}
    </Comp>
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
